/**
 * BoundingGridBox Component
 * 
 * Renders a 3D grid visualization using spheres for depth estimation and spatial reference.
 * Displays two sets of points: original grid points and offset points along the normal vector.
 * Used to visualize depth estimation calculations in the 3D space.
 */

import React from "react";
import { Vector3 } from "three";

/**
 * Props for the BoundingGridBox component
 */
interface BoundingGridBoxProps {
  /** Array of 3D grid points to visualize */
  gridPoints3D: Vector3[];
  /** Normal vector for calculating secondary point positions */
  normal?: Vector3;
  /** Whether the grid visualization should be visible */
  visible: boolean;
}

const BoundingGridBox: React.FC<BoundingGridBoxProps> = ({
  gridPoints3D = [],
  normal,
  visible,
}: BoundingGridBoxProps) => {
  /**
   * Calculates secondary points offset along the normal vector
   * @param points - Original grid points
   * @param normal - Normal vector for offset direction
   * @returns Array of offset points
   */
  const calculateSecondaryPoints = (points: Vector3[], normal?: Vector3): Vector3[] => {
    if (!normal) {
      return [];
    }
    
    const newNormal = normal.clone();
    newNormal.multiplyScalar(5); // Offset distance
    
    return gridPoints3D.map((point: Vector3) => {
      const newPoint = point.clone();
      return newPoint.add(newNormal);
    });
  };

  const pointsPlusNormal = calculateSecondaryPoints(gridPoints3D, normal);

  return (
    <>
      {/* Render original grid points as blue spheres */}
      {visible &&
        gridPoints3D.map((point: Vector3, index: number) => (
          <mesh key={`original-${index}`} position={point}>
            <sphereGeometry args={[0.1, 16, 16]} />
            <meshStandardMaterial color="blue" />
          </mesh>
        ))}
      
      {/* Render offset grid points as red spheres */}
      {visible &&
        pointsPlusNormal.map((point: Vector3, index: number) => (
          <mesh key={`offset-${index}`} position={point}>
            <sphereGeometry args={[0.1, 16, 16]} />
            <meshStandardMaterial color="red" />
          </mesh>
        ))}
    </>
  );
};

export default BoundingGridBox;
